import React, { ReactElement, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';

import { useCreateMembership } from '@data/hooks/membership';
import { useOrganizationChapters } from '@data/hooks/organization';
import { MultiSelectField } from '@ui/components/ui/multi-select';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The red mark beside a label, so a required field is visible before the form is submitted. */
const Req = (): JSX.Element => <span className="text-destructive">*</span>;

/** The value the chapter picker uses for "no default", since a Select cannot hold an empty string. */
const NO_CHAPTER = 'none';

/** Everything an admin can grant. `client` is deliberately absent: a client is not a colleague, and is
 *  added from the Clients page against the products they may read. */
const ASSIGNABLE_ROLES = ['admin', 'productManager', 'projectManager', 'teamMember'] as const;
type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  memberNumber: string;
  defaultChapter: string;
  role: AssignableRole;
};

interface AddMemberDialogProps {
  organizationId: string;
  open: boolean;
  onClose: () => void;
  /** Only an admin may grant a role other than the default; everybody else adds team members. */
  canGrantAnyRole: boolean;
}

/**
 * A name and an email is everything needed to let somebody in. There is no invitation to accept and no
 * password to set: they are a member the moment this saves, and they sign in with Google on the email
 * entered here — so it has to be the one they use with Google.
 */
const AddMemberDialog = ({ organizationId, open, onClose, canGrantAnyRole }: AddMemberDialogProps): ReactElement => {
  const { t } = useTranslation();
  const createMembership = useCreateMembership(organizationId);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      memberNumber: '',
      defaultChapter: NO_CHAPTER,
      role: 'teamMember',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (open)
      reset({
        firstName: '',
        lastName: '',
        email: '',
        memberNumber: '',
        defaultChapter: NO_CHAPTER,
        role: 'teamMember',
      });
  }, [open, reset]);

  const { data: chapters } = useOrganizationChapters(organizationId);

  const onSubmit = ({ memberNumber, defaultChapter, ...values }: FormValues) => {
    // Both are optional, and the API reads "absent" rather than an empty string as no value.
    const payload = {
      ...values,
      ...(memberNumber.trim() && { memberNumber: memberNumber.trim() }),
      ...(defaultChapter !== NO_CHAPTER && { defaultChapter }),
    };
    createMembership.mutate(payload, {
      onSuccess: () => onClose(),
      onError: error => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        setError(status === 400 ? 'email' : 'root', {
          message: status === 400 ? t('addMember.alreadyMember') : t('addMember.failed'),
        });
      },
    });
  };

  const isPending = createMembership.isPending || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{t('addMember.title')}</DialogTitle>
            <DialogDescription>{t('addMember.requiredHint')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Controller
                name="firstName"
                control={control}
                rules={{ required: t('memberDetails.required') }}
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="add-member-first-name">
                      {t('memberDetails.firstName')} <Req />
                    </Label>
                    <Input id="add-member-first-name" {...field} disabled={isPending} autoComplete="off" />
                    {errors.firstName?.message ? (
                      <p className="text-sm text-destructive">{errors.firstName.message}</p>
                    ) : null}
                  </div>
                )}
              />

              <Controller
                name="lastName"
                control={control}
                rules={{ required: t('memberDetails.required') }}
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="add-member-last-name">
                      {t('memberDetails.lastName')} <Req />
                    </Label>
                    <Input id="add-member-last-name" {...field} disabled={isPending} autoComplete="off" />
                    {errors.lastName?.message ? (
                      <p className="text-sm text-destructive">{errors.lastName.message}</p>
                    ) : null}
                  </div>
                )}
              />
            </div>

            <Controller
              name="email"
              control={control}
              rules={{
                required: t('memberDetails.required'),
                pattern: { value: EMAIL_PATTERN, message: t('memberDetails.emailInvalid') },
              }}
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="add-member-email">
                    {t('memberDetails.email')} <Req />
                  </Label>
                  <Input
                    id="add-member-email"
                    type="email"
                    placeholder="name@example.com"
                    {...field}
                    disabled={isPending}
                    autoComplete="off"
                    aria-describedby="add-member-email-hint"
                  />
                  <p id="add-member-email-hint" className="text-xs text-muted-foreground">
                    {t('addMember.emailHint')}
                  </p>
                  {errors.email?.message ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
                </div>
              )}
            />

            <Controller
              name="memberNumber"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="add-member-number">
                    {t('memberNumber')} <span className="font-normal text-muted-foreground">({t('optional')})</span>
                  </Label>
                  <Input id="add-member-number" {...field} disabled={isPending} autoComplete="off" />
                </div>
              )}
            />

            <Controller
              name="defaultChapter"
              control={control}
              render={({ field }) => (
                <MultiSelectField
                  single
                  label={
                    <>
                      {t('defaultChapter')} <span className="font-normal text-muted-foreground">({t('optional')})</span>
                    </>
                  }
                  options={(chapters?.items ?? []).map(chapter => ({ value: chapter.key, label: chapter.label }))}
                  value={field.value === NO_CHAPTER ? [] : [field.value]}
                  onChange={next => field.onChange(next[0] ?? NO_CHAPTER)}
                  emptyLabel={t('memberDetails.noDefaultChapter')}
                  searchPlaceholder={t('search')}
                  className="max-w-xs"
                />
              )}
            />

            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <MultiSelectField
                  single
                  label={t('permissions')}
                  options={ASSIGNABLE_ROLES.map(role => ({ value: role, label: t(`roles.${role}`) }))}
                  value={[field.value]}
                  onChange={next => field.onChange((next[0] as AssignableRole) ?? 'teamMember')}
                  emptyLabel={t('permissions')}
                  // Four options, and a role can never be absent: no search box, and no clear.
                  searchable={false}
                  clearable={false}
                  disabled={isPending || !canGrantAnyRole}
                  className="max-w-xs"
                />
              )}
            />
            {!canGrantAnyRole ? <p className="text-xs text-muted-foreground">{t('nonAdminRoleLimit')}</p> : null}

            {errors.root?.message ? <p className="text-sm text-destructive">{errors.root.message}</p> : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !isValid}>
              {t('addMember.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMemberDialog;
